import {
  createCapacitorHapticsAdapter,
  createCapacitorAdMobService,
  createGridFlowGame,
  createMockAdService,
  isCapacitorAdMobPluginAvailable,
  parseColorVisionProfile,
  type AdService,
  type CapacitorHapticsLike,
  type CapacitorAdMobAdConfig,
  type GridFlowGameOptions
} from "@gridflow/game";
import { AdMob } from "@capacitor-community/admob";
import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics";
import { StatusBar, Style } from "@capacitor/status-bar";
import { resolveBootStateLabel, type BootState } from "./boot-state";
import "./styles.css";

const host = document.querySelector<HTMLDivElement>("#app");

if (host === null) {
  throw new Error("GridFlow host element was not found.");
}

const gameHost = host;
const searchParams = new URLSearchParams(window.location.search);
const levelId = searchParams.get("level");
const hintThresholdMs = parseFiniteInteger(searchParams.get("hintThresholdMs"));
const nativeShellEnabled = Capacitor.isNativePlatform();
const nativePlatform = Capacitor.getPlatform();
const ANDROID_BOTTOM_SAFE_INSET = 72;

void configureNativeShell(nativeShellEnabled);

const adService = createNativeAdService();
const shouldPrimeNativeAdConsent =
  nativeShellEnabled && nativePlatform === "android" && isCapacitorAdMobPluginAvailable(AdMob);

const gameOptions: GridFlowGameOptions = {
  colorVisionProfile: parseColorVisionProfile(searchParams.get("cvdProfile")),
  ...(nativePlatform === "android" ? { bottomSafeInset: ANDROID_BOTTOM_SAFE_INSET } : {}),
  ...(levelId ? { levelId } : {}),
  ...(hintThresholdMs !== null ? { hintStuckThresholdMs: hintThresholdMs } : {}),
  ...(nativeShellEnabled ? { haptics: createCapacitorHapticsAdapter(createNativeHaptics()) } : {}),
  adService,
  ...(searchParams.has("hintReady") ? { forceHintAvailable: true } : {}),
  useColorBlindPalette: searchParams.has("cvd"),
  preserveDrawingBuffer: searchParams.has("qaCapture")
};

let activeBootAttempt: Promise<void> | null = null;

gameHost.addEventListener("click", retryBootAfterFailure);
gameHost.addEventListener("keydown", handleBootRetryKeydown);

void bootGridFlowGame();

function bootGridFlowGame(): Promise<void> {
  if (activeBootAttempt !== null) {
    return activeBootAttempt;
  }

  const attempt = runBootAttempt();
  activeBootAttempt = attempt;
  void attempt.finally(() => {
    if (activeBootAttempt === attempt) {
      activeBootAttempt = null;
    }
  });

  return activeBootAttempt;
}

async function runBootAttempt(): Promise<void> {
  setBootState("loading");
  gameHost.replaceChildren();

  try {
    await createGridFlowGame(gameHost, {
      ...gameOptions,
      ...(searchParams.has("reducedMotion") ? { reducedMotion: true } : {})
    });
    primeNativeAdConsent();
    setBootState("ready");
  } catch (error: unknown) {
    setBootState("failed");
    console.error("GridFlow failed to boot.", error);
  }
}

function retryBootAfterFailure(): void {
  if (gameHost.dataset.bootState !== "failed") {
    return;
  }

  void bootGridFlowGame();
}

function handleBootRetryKeydown(event: KeyboardEvent): void {
  if (event.key !== "Enter" && event.key !== " ") {
    return;
  }

  if (gameHost.dataset.bootState !== "failed") {
    return;
  }

  event.preventDefault();
  void bootGridFlowGame();
}

function setBootState(state: BootState): void {
  gameHost.dataset.bootState = state;
  gameHost.dataset.bootLabel = resolveBootStateLabel(state);
  gameHost.setAttribute(
    "aria-label",
    state === "ready" ? "GridFlow canvas stage" : resolveBootStateLabel(state)
  );

  if (state === "failed") {
    gameHost.setAttribute("role", "button");
    gameHost.tabIndex = 0;
    return;
  }

  gameHost.removeAttribute("role");
  gameHost.removeAttribute("tabindex");
}

function parseFiniteInteger(value: string | null): number | null {
  if (value === null) {
    return null;
  }
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

async function configureNativeShell(enabled: boolean): Promise<void> {
  if (!enabled) {
    return;
  }

  await Promise.allSettled([
    StatusBar.setStyle({ style: Style.Dark }),
    StatusBar.setBackgroundColor({ color: "#07090d" }),
    StatusBar.setOverlaysWebView({ overlay: false })
  ]);
}

function createNativeAdService(): AdService {
  if (
    !nativeShellEnabled ||
    nativePlatform !== "android" ||
    !isCapacitorAdMobPluginAvailable(AdMob)
  ) {
    return createMockAdService();
  }

  return createCapacitorAdMobService({
    plugin: AdMob,
    config: resolveNativeAdMobConfig()
  });
}

function resolveNativeAdMobConfig(): CapacitorAdMobAdConfig {
  if (import.meta.env.VITE_GRIDFLOW_AD_MODE !== "production") {
    return { mode: "test" };
  }

  return {
    mode: "production",
    rewardedHintAdUnitId: import.meta.env.VITE_GRIDFLOW_REWARDED_HINT_AD_UNIT_ID,
    interstitialLevelTransitionAdUnitId: import.meta.env
      .VITE_GRIDFLOW_INTERSTITIAL_LEVEL_TRANSITION_AD_UNIT_ID,
    rewardedHintAlias: import.meta.env.VITE_GRIDFLOW_REWARDED_HINT_AD_UNIT_ALIAS,
    interstitialLevelTransitionAlias: import.meta.env
      .VITE_GRIDFLOW_INTERSTITIAL_LEVEL_TRANSITION_AD_UNIT_ALIAS
  };
}

function primeNativeAdConsent(): void {
  if (!shouldPrimeNativeAdConsent) {
    return;
  }

  void adService.requestConsent().catch((error: unknown) => {
    console.warn("GridFlow AdMob consent initialization failed.", error);
  });
}

function createNativeHaptics(): CapacitorHapticsLike {
  return {
    selectionChanged() {
      return Haptics.selectionChanged();
    },
    impact(options) {
      return Haptics.impact({ style: resolveImpactStyle(options?.style) });
    },
    notification(options) {
      return Haptics.notification({ type: resolveNotificationType(options?.type) });
    }
  };
}

function resolveImpactStyle(style: "LIGHT" | "MEDIUM" | "HEAVY" | undefined): ImpactStyle {
  switch (style) {
    case "HEAVY":
      return ImpactStyle.Heavy;
    case "MEDIUM":
      return ImpactStyle.Medium;
    case "LIGHT":
    default:
      return ImpactStyle.Light;
  }
}

function resolveNotificationType(
  type: "SUCCESS" | "WARNING" | "ERROR" | undefined
): NotificationType {
  switch (type) {
    case "ERROR":
      return NotificationType.Error;
    case "SUCCESS":
      return NotificationType.Success;
    case "WARNING":
    default:
      return NotificationType.Warning;
  }
}
